import React from 'react';
import { ArrowRight, Check, AlertCircle } from 'lucide-react';
import { LEAD_FIELDS } from '@/types/import';
import { cn } from '@/lib/utils';

interface SuggestedMapping {
  csvColumn: string;
  leadField: string;
  confidence: number;
}

interface ColumnMapperProps {
  csvColumns: string[];
  sampleData: Record<string, string>[];
  suggestedMapping: SuggestedMapping[];
  fieldMapping: Record<string, string>;
  onMappingChange: (csvColumn: string, leadField: string) => void;
}

export const ColumnMapper: React.FC<ColumnMapperProps> = ({
  csvColumns,
  sampleData,
  suggestedMapping,
  fieldMapping,
  onMappingChange,
}) => {
  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.8) return 'bg-green-100 text-green-800';
    if (confidence >= 0.5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getSuggestedField = (csvColumn: string): SuggestedMapping | undefined => {
    return suggestedMapping.find(s => s.csvColumn === csvColumn);
  };

  const isRequiredFieldMapped = (field: string): boolean => {
    return Object.values(fieldMapping).includes(field);
  };

  const requiredFields = LEAD_FIELDS.filter(f => f.required);
  const missingRequiredFields = requiredFields.filter(f => !isRequiredFieldMapped(f.value));

  return (
    <div className="space-y-6">
      {/* Required fields warning */}
      {missingRequiredFields.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Missing required fields</p>
              <p className="text-sm text-yellow-700 mt-1">
                Please map the following required fields: {missingRequiredFields.map(f => f.label).join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Column mapping table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                CSV Column
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">

              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lead Field
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sample Data
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {csvColumns.map((column, index) => {
              const suggested = getSuggestedField(column);
              const currentMapping = fieldMapping[column] || '';
              const isMapped = currentMapping !== '';
              const isSkipped = currentMapping === '';

              return (
                <tr key={column} className={cn(
                  'hover:bg-gray-50',
                  index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                )}>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">{column}</span>
                      {suggested && suggested.confidence >= 0.5 && (
                        <span className={cn(
                          'text-xs px-2 py-0.5 rounded-full',
                          getConfidenceColor(suggested.confidence)
                        )}>
                          {Math.round(suggested.confidence * 100)}% match
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <ArrowRight className={cn(
                      'w-4 h-4 mx-auto',
                      isMapped ? 'text-green-500' : 'text-gray-300'
                    )} />
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={currentMapping}
                      onChange={(e) => onMappingChange(column, e.target.value)}
                      className={cn(
                        'w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                        isMapped ? 'border-green-300 bg-green-50' : 'border-gray-300',
                        isSkipped && 'text-gray-400'
                      )}
                    >
                      {LEAD_FIELDS.map((field) => (
                        <option key={field.value} value={field.value}>
                          {field.label}
                          {field.required && ' *'}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-500 truncate max-w-xs">
                      {sampleData.length > 0 && sampleData[0][column] ? (
                        <span title={sampleData[0][column]}>
                          {sampleData[0][column]}
                        </span>
                      ) : (
                        <span className="text-gray-300 italic">No data</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Sample data preview */}
      {sampleData.length > 1 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Data Preview (First 3 rows)</h4>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">#</th>
                  {csvColumns.slice(0, 5).map((col) => (
                    <th key={col} className="px-3 py-2 text-left text-xs font-medium text-gray-500 truncate max-w-[120px]">
                      {col}
                    </th>
                  ))}
                  {csvColumns.length > 5 && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                      +{csvColumns.length - 5} more
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sampleData.slice(0, 3).map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-400">{index + 1}</td>
                    {csvColumns.slice(0, 5).map((col) => (
                      <td key={col} className="px-3 py-2 text-gray-600 truncate max-w-[120px]" title={row[col]}>
                        {row[col] || '-'}
                      </td>
                    ))}
                    {csvColumns.length > 5 && (
                      <td className="px-3 py-2 text-gray-400">...</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Mapping summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Check className="w-4 h-4 text-green-500" />
              <span className="text-gray-600">
                {Object.values(fieldMapping).filter(v => v !== '').length} columns mapped
              </span>
            </div>
            <div className="flex items-center space-x-2 text-gray-400">
              <span>{Object.values(fieldMapping).filter(v => v === '').length} columns skipped</span>
            </div>
          </div>
          {missingRequiredFields.length === 0 && (
            <span className="text-green-600 font-medium">All required fields mapped</span>
          )}
        </div>
      </div>
    </div>
  );
};
